import React, { useState } from "react";
import { createRoot, Root } from "react-dom/client";
import {
  MessageProps,
  MessageShowConfig,
  MessageHandler,
  MessageMethods,
  MessageType,
} from "./Message.types";
import "./Message.scss";
import { MessageCloseIcon } from "../../../components/Icon/IconSvgMap";
import SuccessedImg from "@/assets/message/successed.png";
import FailedImg from "@/assets/message/failed.png";
import LoadingImg from "@/assets/message/loading.png";
import { translate } from "../../../i18n/utils";

// 声明全局 i18next
declare global {
  interface Window {
    i18next?: {
      t: (key: string) => string;
    };
  }
}

/**
 * 获取翻译文本
 */
const getTranslation = (key: string, defaultValue: string): string => {
  if (typeof window !== "undefined" && window.i18next) {
    return window.i18next.t(key);
  }
  return defaultValue;
};

/**
 * 默认图标组件
 */
const DefaultIcons = {
  success: (
    // <svg
    //   viewBox="0 0 24 24"
    //   fill="none"
    //   xmlns="http://www.w3.org/2000/svg"
    //   style={{ width: "24px", height: "24px" }}
    // >
    //   <path
    //     d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
    //     stroke="currentColor"
    //     strokeWidth="2"
    //     strokeLinecap="round"
    //     strokeLinejoin="round"
    //     fill="white"
    //   />
    // </svg>
    <img className="" src={SuccessedImg} alt="Android" />
  ),
  failed: <img className="" src={FailedImg} alt="Android" />,
  loading: <img className="" src={LoadingImg} alt="Android" />,
};

/**
 * 默认关闭图标
 */
const CloseIcon = <MessageCloseIcon />;

/**
 * 获取默认文本
 */
const getDefaultContent = (type: MessageType): string => {
  switch (type) {
    case "success":
      return getTranslation("message.success", "Success");
    case "failed":
      return getTranslation("message.failed", "Failed");
    case "loading":
      return getTranslation("message.loading", "Loading...");
    default:
      return "";
  }
};

/**
 * Message组件
 */
const MessageComponent: React.FC<
  MessageProps & { visible: boolean; top: number }
> = ({
  type,
  content,
  leftIcon,
  rightIcon,
  showClose = true,
  onClose,
  className = "",
  style = {},
  top,
}) => {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose?.();
    }, 300); // 等待动画结束
  };

  const displayContent = content || getDefaultContent(type);
  const displayLeftIcon = leftIcon || DefaultIcons[type];
  const displayRightIcon = rightIcon || CloseIcon;

  return (
    <div
      className={`message message--${type} ${isLeaving ? "message--leaving" : ""} ${className}`}
      style={{ ...style, top: `${top}px` }}
    >
      {/* 左侧图标 */}
      <div className="message__left-icon">{displayLeftIcon}</div>
      {/* 中间文本 */}
      <div className="message__content">{displayContent}</div>
      {/* 右侧关闭按钮 */}
      {showClose && (
        <div className="message__right-icon" onClick={handleClose}>
          {displayRightIcon}
        </div>
      )}
    </div>
  );
};

/**
 * 消息项接口
 */
interface MessageItem {
  id: string;
  config: MessageShowConfig;
  timer?: NodeJS.Timeout;
}

/**
 * Message容器管理
 */
class MessageManager {
  private container: HTMLDivElement | null = null;
  private root: Root | null = null;
  private messages: MessageItem[] = [];
  private messageIdCounter = 0;
  private readonly MESSAGE_HEIGHT = 60; // 每个消息的高度（包括间距）
  private readonly INITIAL_TOP = 58; // 初始top值

  /**
   * 获取或创建容器
   */
  private getContainer(): HTMLDivElement {
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.className = "message-container";
      this.container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        z-index: 9999;
      `;
      document.body.appendChild(this.container);
      this.root = createRoot(this.container);
    }
    return this.container;
  }

  /**
   * 渲染所有消息
   */
  private render() {
    const container = this.getContainer();

    if (this.messages.length > 0) {
      container.style.pointerEvents = "auto";
    } else {
      container.style.pointerEvents = "none";
    }

    if (this.root) {
      this.root.render(
        <>
          {this.messages.map((item, index) => {
            const {
              type,
              content,
              leftIcon,
              rightIcon,
              showClose = true,
              className,
              style,
            } = item.config;

            // 计算每个消息的top位置
            const top = this.INITIAL_TOP + index * this.MESSAGE_HEIGHT;

            return (
              <MessageComponent
                key={item.id}
                type={type}
                content={translate(content as string)}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                showClose={showClose}
                onClose={() => this.removeMessage(item.id)}
                className={className}
                style={style}
                top={top}
                visible={true}
              />
            );
          })}
        </>
      );
    }
  }

  /**
   * 移除消息
   */
  private removeMessage(id: string) {
    const messageIndex = this.messages.findIndex(msg => msg.id === id);
    if (messageIndex === -1) return;

    const message = this.messages[messageIndex];

    // 清除定时器
    if (message.timer) {
      clearTimeout(message.timer);
    }

    // 调用用户的onClose回调
    message.config.onClose?.();

    // 从数组中移除
    this.messages.splice(messageIndex, 1);

    // 重新渲染
    this.render();
  }

  /**
   * 显示消息
   */
  show(config: MessageShowConfig): MessageHandler {
    const { duration = 3000 } = config;

    // 生成唯一ID
    const id = `message-${++this.messageIdCounter}`;

    // 创建消息项
    const messageItem: MessageItem = {
      id,
      config,
    };

    // 添加到消息队列
    this.messages.push(messageItem);

    // 设置自动关闭
    if (duration > 0) {
      messageItem.timer = setTimeout(() => {
        this.removeMessage(id);
      }, duration);
    }

    // 渲染所有消息
    this.render();

    return {
      close: () => this.removeMessage(id),
    };
  }

  /**
   * 清除所有消息
   */
  clear() {
    // 清除所有定时器
    this.messages.forEach(message => {
      if (message.timer) {
        clearTimeout(message.timer);
      }
    });

    // 清空消息数组
    this.messages = [];

    // 重新渲染
    this.render();
  }
}

// 创建全局实例
const messageManager = new MessageManager();

/**
 * Message静态方法
 */
export const Message: MessageMethods = {
  /**
   * 显示消息
   */
  show: (config: MessageShowConfig | string): MessageHandler => {
    if (typeof config === "string") {
      return messageManager.show({
        type: "success",
        content: config,
      });
    }
    return messageManager.show(config);
  },

  /**
   * 成功消息
   */
  success: (content, duration = 3000): MessageHandler => {
    return messageManager.show({
      type: "success",
      content,
      duration,
    });
  },

  /**
   * 失败消息
   */
  failed: (content, duration = 3000): MessageHandler => {
    return messageManager.show({
      type: "failed",
      content,
      duration,
    });
  },

  /**
   * 加载消息
   */
  loading: (content, duration = 3000): MessageHandler => {
    return messageManager.show({
      type: "loading",
      content,
      duration,
    });
  },

  /**
   * 清除所有消息
   */
  clear: (): void => {
    messageManager.clear();
  },
};

export type { MessageProps, MessageShowConfig, MessageHandler, MessageMethods };
